/**
 * <blog-articles> — search, sort + paginate the Blog articles section across every page.
 *
 * Shopify only serves blog articles newest first and Liquid cannot read the query string, so the
 * server render is always "Date (Newest First)". Any other order lives in ?sort_by=<value>: this
 * element fetches every card through the ?view=articles-data template (50 per request), sorts
 * them, and renders the requested ?page= itself with the same pagination markup as
 * snippets/pagination.liquid. A search (?q=) filters that same full list by title, category,
 * excerpt and tags. With no search and the default order it hands back to the server render.
 *
 * All Articles mode (data-sources="/blogs/a,/blogs/b"): the list is merged from several blogs.
 * The server can only render page 1 of a merged list (data-server-paginates="false"), so every
 * other page is rendered here too, even with the default order and no search.
 *
 * Progressive enhancement: the sort control stays hidden without JS, and the search form
 * submits to Shopify's /search for articles.
 */
class BlogArticles extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-grid]');
    this.paginationEl = this.querySelector('[data-pagination]');
    this.sortWrap = this.querySelector('[data-sort]');
    this.select = this.querySelector('[data-sort-select]');
    this.caret = this.querySelector('template[data-caret]');
    this.toolbar = this.querySelector('.ba-toolbar');
    this.countEl = this.querySelector('[data-count]');
    this.searchForm = this.querySelector('[data-search]');
    this.searchInput = this.querySelector('[data-search-input]');
    this.searchClear = this.querySelector('.ba-search [data-search-clear]');
    this.noResults = this.querySelector('[data-no-results]');
    this.noResultsQuery = this.querySelector('[data-no-results-query]');
    if (!this.grid || !this.paginationEl || (!this.select && !this.searchInput)) return;

    this.serverCount = this.countEl ? this.countEl.textContent : '';

    this.perPage = parseInt(this.dataset.perPage, 10) || 8;
    this.defaultSort = this.dataset.defaultSort || 'date-desc';
    this.validSorts = this.select ? Array.from(this.select.options).map((option) => option.value) : [this.defaultSort];
    this.items = null;
    this.sources = (this.dataset.sources || '').split(',').map((path) => path.trim()).filter(Boolean);
    if (!this.sources.length) this.sources = [window.location.pathname];
    this.serverPaginates = this.dataset.serverPaginates !== 'false';
    this.state = this.readUrl();

    if (this.select) {
      this.sortWrap.hidden = false;
      this.select.value = this.state.sort;
      this.select.addEventListener('change', this.onSortChange.bind(this));
    }

    if (this.searchInput) {
      this.searchInput.value = this.state.q;
      this.searchInput.addEventListener('input', this.onSearchInput.bind(this));
      // Start loading on focus so the first keystroke already has the full list
      this.searchInput.addEventListener('focus', () => this.loadAll().catch(() => {}), { once: true });
      this.searchForm.addEventListener('submit', (event) => {
        event.preventDefault();
        clearTimeout(this.searchTimer);
        this.applySearch();
      });
    }

    this.querySelectorAll('[data-search-clear]').forEach((button) => {
      button.addEventListener('click', this.clearSearch.bind(this));
    });

    this.paginationEl.addEventListener('click', this.onPaginationClick.bind(this));

    this.onPopState = this.onPopState.bind(this);
    window.addEventListener('popstate', this.onPopState);

    this.syncClear();
    if (!this.isServerState(this.state)) this.show(false);
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopState);
  }

  readUrl() {
    const params = new URLSearchParams(window.location.search);
    const sort = params.get('sort_by');
    const page = parseInt(params.get('page'), 10) || 1;
    const q = (params.get('q') || '').trim();
    return { sort: this.validSorts.includes(sort) ? sort : this.defaultSort, q, page };
  }

  /** Newest first with no search is exactly what Shopify rendered for this URL */
  isServerState(state) {
    return state.sort === this.defaultSort && !state.q && (this.serverPaginates || state.page <= 1);
  }

  buildUrl(state) {
    const url = new URL(window.location.href);
    ['page', 'sort_by', 'q'].forEach((key) => url.searchParams.delete(key));
    if (state.q) url.searchParams.set('q', state.q);
    if (state.sort !== this.defaultSort) url.searchParams.set('sort_by', state.sort);
    if (state.page > 1) url.searchParams.set('page', state.page);
    return url.toString();
  }

  /** Moves to a new state. Back to the plain server state means reloading that page. */
  go(next, { push = true, scroll = false } = {}) {
    const wasServer = this.isServerState(this.state);
    this.state = next;
    const url = this.buildUrl(next);

    if (this.isServerState(next) && (wasServer || next.page > 1 || !this.items)) {
      window.location.assign(url);
      return;
    }

    window.history[push ? 'pushState' : 'replaceState']({ blogArticles: true }, '', url);
    this.show(scroll);
  }

  onSortChange() {
    this.go({ ...this.state, sort: this.select.value, page: 1 });
  }

  onSearchInput() {
    this.syncClear();
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.applySearch(), 250);
  }

  applySearch() {
    const q = this.searchInput.value.trim();
    if (q === this.state.q) return;
    // Typing should not leave one history entry per keystroke
    this.go({ ...this.state, q, page: 1 }, { push: false });
  }

  clearSearch() {
    if (!this.searchInput) return;
    this.searchInput.value = '';
    this.syncClear();
    this.searchInput.focus();
    if (this.state.q) this.go({ ...this.state, q: '', page: 1 }, { push: false });
  }

  syncClear() {
    if (this.searchClear) this.searchClear.hidden = !this.searchInput.value;
  }

  onPaginationClick(event) {
    const link = event.target.closest('a[data-page]');
    if (!link) return;
    // A single blog's own pages are real server pages; a merged list only has page 1
    if (this.serverPaginates && this.isServerState(this.state)) return;

    event.preventDefault();
    this.go({ ...this.state, page: parseInt(link.dataset.page, 10) }, { scroll: true });
  }

  onPopState() {
    const next = this.readUrl();

    // The server render for that URL is the source of truth.
    if (this.isServerState(next)) {
      window.location.reload();
      return;
    }

    this.state = next;
    if (this.select) this.select.value = next.sort;
    if (this.searchInput) this.searchInput.value = next.q;
    this.syncClear();
    this.show(true);
  }

  async show(scroll) {
    const { sort, q, page } = this.state;
    this.setAttribute('aria-busy', 'true');

    try {
      const matches = this.filterItems(await this.loadAll(), q);
      // A newer state may have arrived while the list was loading
      if (this.state.sort !== sort || this.state.q !== q || this.state.page !== page) return;

      const sorted = this.sortItems(matches, sort);
      const pages = Math.max(1, Math.ceil(sorted.length / this.perPage));
      const current = Math.min(Math.max(page, 1), pages);
      const start = (current - 1) * this.perPage;

      this.grid.replaceChildren(...sorted.slice(start, start + this.perPage).map((node) => node.cloneNode(true)));
      this.grid.hidden = sorted.length === 0;
      this.renderCount(sorted.length, q);
      this.renderNoResults(sorted.length, q);
      this.renderPagination(current, pages);

      if (scroll && this.toolbar) this.toolbar.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (error) {
      // Leave the server-rendered newest-first list in place
      console.error('Blog articles failed to load', error);
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  normalize(text) {
    return (text || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, ' ');
  }

  /** Every word of the query must appear somewhere in the card text or its tags */
  filterItems(items, q) {
    const words = this.normalize(q).split(' ').filter(Boolean);
    if (!words.length) return items;

    return items.filter((node) => {
      if (node.searchText === undefined) {
        node.searchText = this.normalize(`${node.textContent} ${node.dataset.tags || ''}`);
      }
      return words.every((word) => node.searchText.includes(word));
    });
  }

  renderCount(total, q) {
    if (!this.countEl) return;
    if (this.isServerState(this.state)) {
      this.countEl.textContent = this.serverCount;
      return;
    }

    const { label, labelSingle } = this.countEl.dataset;
    let text = `${total} ${total === 1 ? labelSingle : label}`;
    if (q) text += ` ${this.countEl.dataset.for || 'for'} “${q}”`;
    this.countEl.textContent = text;
  }

  renderNoResults(total, q) {
    if (!this.noResults) return;
    this.noResults.hidden = total > 0;
    if (this.noResultsQuery) this.noResultsQuery.textContent = q ? ` “${q}”` : '';
  }

  /** Every card from every source blog, fetched once (concurrent callers share the request) */
  loadAll() {
    if (!this.itemsPromise) {
      this.itemsPromise = this.fetchAll().catch((error) => {
        this.itemsPromise = null;
        throw error;
      });
    }
    return this.itemsPromise;
  }

  async fetchAll() {
    const fetchPage = async (path, page) => {
      const url = new URL(path, window.location.origin);
      url.searchParams.set('view', 'articles-data');
      url.searchParams.set('page', page);
      const response = await fetch(url.toString(), { credentials: 'same-origin' });
      if (!response.ok) throw new Error(`HTTP ${response.status} for ${path}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      return doc.querySelector('[data-articles-data]');
    };

    const fetchSource = async (path) => {
      const first = await fetchPage(path, 1);
      if (!first) throw new Error(`articles-data view missing for ${path}`);
      const totalPages = parseInt(first.dataset.pages, 10) || 1;
      const rest = await Promise.all(Array.from({ length: totalPages - 1 }, (_, i) => fetchPage(path, i + 2)));
      return [first, ...rest];
    };

    const wrappers = (await Promise.all(this.sources.map(fetchSource))).flat();

    this.items = wrappers
      .filter(Boolean)
      .flatMap((wrapper) => Array.from(wrapper.querySelectorAll('[data-article]')))
      .map((node) => document.importNode(node, true));

    return this.items;
  }


  sortItems(items, sort) {
    const byDate = (a, b) => Number(a.dataset.date) - Number(b.dataset.date);
    const byTitle = (a, b) => a.dataset.title.localeCompare(b.dataset.title, undefined, { sensitivity: 'base', numeric: true });
    const list = items.slice();

    switch (sort) {
      case 'date-asc':
        return list.sort(byDate);
      case 'title-asc':
        return list.sort(byTitle);
      case 'title-desc':
        return list.sort((a, b) => byTitle(b, a));
      default:
        return list.sort((a, b) => byDate(b, a));
    }
  }

  /** First, last, current ±2 and the first/last five at the edges; gaps become an ellipsis */
  pageParts(current, pages) {
    const parts = [];
    let last = 0;
    for (let page = 1; page <= pages; page += 1) {
      const nearEdge = (current <= 3 && page <= 5) || (current >= pages - 2 && page >= pages - 4);
      if (page === 1 || page === pages || Math.abs(page - current) <= 2 || nearEdge) {
        if (last && page - last > 1) parts.push('…');
        parts.push(page);
        last = page;
      }
    }
    return parts;
  }

  renderPagination(current, pages) {
    if (pages <= 1) {
      this.paginationEl.replaceChildren();
      return;
    }

    const caret = this.caret ? this.caret.innerHTML : '';
    const href = (page) => this.buildUrl({ ...this.state, page });
    const items = [];

    if (current > 1) {
      items.push(
        `<li><a href="${href(current - 1)}" data-page="${current - 1}" class="pagination__item pagination__item--next pagination__item-arrow link motion-reduce" aria-label="Previous page"><span class="svg-wrapper">${caret}</span></a></li>`
      );
    }

    this.pageParts(current, pages).forEach((part) => {
      if (part === '…') {
        items.push('<li><span class="pagination__item">…</span></li>');
      } else if (part === current) {
        items.push(
          `<li><a role="link" aria-disabled="true" class="pagination__item pagination__item--current light" aria-current="page" aria-label="Page ${part}">${part}</a></li>`
        );
      } else {
        items.push(`<li><a href="${href(part)}" data-page="${part}" class="pagination__item link" aria-label="Page ${part}">${part}</a></li>`);
      }
    });

    if (current < pages) {
      items.push(
        `<li><a href="${href(current + 1)}" data-page="${current + 1}" class="pagination__item pagination__item--prev pagination__item-arrow link motion-reduce" aria-label="Next page"><span class="svg-wrapper">${caret}</span></a></li>`
      );
    }

    this.paginationEl.innerHTML = `<div class="pagination-wrapper" data-page="${current}"><nav class="pagination" role="navigation" aria-label="Pagination"><ul class="pagination__list list-unstyled" role="list">${items.join('')}</ul></nav></div>`;
  }
}

if (!customElements.get('blog-articles')) {
  customElements.define('blog-articles', BlogArticles);
}
